import { z } from 'zod';
import {
  AUDIENCES, PRICING_TYPES, MEDIA_CATEGORIES, MEDIA_TYPES, USER_ROLES,
  SECTION_TYPES, PAGE_TYPES, ENQUIRY_STATUSES, MENU_LOCATIONS, CTA_TYPES,
  WEEKDAYS, WHATSAPP_TEMPLATE_KEYS, MAX_PAGE_SIZE,
} from '../constants/index.js';

/** A Mongo ObjectId as it appears over the wire. */
export const objectId = z.string().regex(/^[a-f0-9]{24}$/i, 'Must be a valid id');

export const slug = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and hyphens');

/** Site-relative path, always leading and trailing slash (matches the legacy URLs). */
export const sitePath = z
  .string()
  .regex(/^\/(?:[a-z0-9\-._~/]*)?$/i, 'Must be a site-relative path starting with /');

export const trimmed = (max = 500) => z.string().trim().max(max);
export const optionalText = (max = 500) => trimmed(max).default('');

export const mediaRefSchema = z.object({
  mediaId: objectId.nullable().default(null),
  url: z.string().max(2048).nullable().default(null),
  alt: z.string().max(500).nullable().default(''),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional(),
}).nullable().default(null);

export const seoSchema = z.object({
  title: optionalText(200),
  description: optionalText(400),
  keywords: z.array(trimmed(80)).max(30).default([]),
  canonical: z.string().max(2048).default(''),
  ogImage: mediaRefSchema,
  noindex: z.boolean().default(false),
}).default({
  title: '', description: '', keywords: [], canonical: '', ogImage: null, noindex: false,
});

// ---------------------------------------------------------------- branches

export const openingHoursDaySchema = z.object({
  day: z.enum(WEEKDAYS),
  closed: z.boolean().default(false),
  opens: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM').default('08:00'),
  closes: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM').default('21:30'),
});

export const holidayHoursSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
  label: optionalText(120),
  closed: z.boolean().default(false),
  opens: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
  closes: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
});

/** Indian mobile numbers in E.164 without the plus, e.g. 919008718333. */
export const phoneE164 = z
  .string()
  .trim()
  .regex(/^\d{10,15}$/, 'Digits only, including country code (e.g. 919008718333)');

export const branchSchema = z.object({
  name: trimmed(160),
  slug,
  shortName: optionalText(80),
  displayNameLines: z.array(trimmed(80)).max(4).default([]),
  nameAccent: optionalText(80),
  addressLines: z.array(trimmed(160)).max(8).default([]),
  locality: optionalText(120),
  city: optionalText(120).default('Bengaluru'),
  state: optionalText(120).default('Karnataka'),
  postalCode: optionalText(20),
  country: optionalText(80).default('India'),
  phone: phoneE164,
  phoneDisplay: optionalText(40),
  whatsappNumber: phoneE164,
  email: z.string().email().or(z.literal('')).default(''),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
  mapsUrl: z.string().max(2048).default(''),
  directionsUrl: z.string().max(2048).default(''),
  googlePlaceId: optionalText(120),
  openingHours: z.array(openingHoursDaySchema).max(7).default([]),
  holidayHours: z.array(holidayHoursSchema).max(60).default([]),
  description: optionalText(2000),
  areaTagline: optionalText(300),
  heroImage: mediaRefSchema,
  serviceAreas: z.array(trimmed(80)).max(40).default([]),
  order: z.number().int().min(0).default(0),
  enabled: z.boolean().default(true),
  openBadgeLabel: optionalText(60).default('Open Now'),
});

// ---------------------------------------------------------------- services

export const serviceCategorySchema = z.object({
  name: trimmed(160),
  nameAccent: optionalText(80),
  slug,
  audience: z.enum(AUDIENCES),
  description: optionalText(1500),
  image: mediaRefSchema,
  icon: optionalText(40),
  order: z.number().int().min(0).default(0),
  active: z.boolean().default(true),
  inMainMenu: z.boolean().default(true),
  note: optionalText(400),
});

export const serviceVariantSchema = z.object({
  name: trimmed(120),
  label: optionalText(120),
  price: z.number().min(0).nullable().default(null),
  minPrice: z.number().min(0).nullable().default(null),
  maxPrice: z.number().min(0).nullable().default(null),
  available: z.boolean().default(true),
});

export const branchAvailabilitySchema = z.object({
  branchId: objectId,
  enabled: z.boolean().default(true),
  priceOverride: z.number().min(0).nullable().default(null),
  variants: z.array(serviceVariantSchema).max(20).default([]),
});

export const serviceSchema = z.object({
  categoryId: objectId,
  name: trimmed(200),
  slug,
  audience: z.enum(AUDIENCES),
  description: optionalText(2000),
  shortDescription: optionalText(400),
  pricingType: z.enum(PRICING_TYPES).default('fixed'),
  basePrice: z.number().min(0).nullable().default(null),
  minPrice: z.number().min(0).nullable().default(null),
  maxPrice: z.number().min(0).nullable().default(null),
  variants: z.array(serviceVariantSchema).max(20).default([]),
  displayPriceText: optionalText(200),
  variantLabel: optionalText(200),
  currency: optionalText(8).default('INR'),
  branchAvailability: z.array(branchAvailabilitySchema).max(20).default([]),
  tags: z.array(trimmed(60)).max(30).default([]),
  image: mediaRefSchema,
  featured: z.boolean().default(false),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).default(0),
  seo: seoSchema,
}).superRefine((v, ctx) => {
  // A price must exist in whatever shape the pricing type promises.
  if (['fixed', 'starting-from'].includes(v.pricingType) && v.basePrice == null) {
    ctx.addIssue({ code: 'custom', path: ['basePrice'], message: `basePrice is required for pricingType "${v.pricingType}"` });
  }
  if (v.pricingType === 'range' && (v.minPrice == null || v.maxPrice == null)) {
    ctx.addIssue({ code: 'custom', path: ['minPrice'], message: 'minPrice and maxPrice are required for a range price' });
  }
  if (v.pricingType === 'range' && v.minPrice != null && v.maxPrice != null && v.minPrice > v.maxPrice) {
    ctx.addIssue({ code: 'custom', path: ['maxPrice'], message: 'maxPrice must be at least minPrice' });
  }
  if (v.pricingType === 'variants' && v.variants.length === 0) {
    ctx.addIssue({ code: 'custom', path: ['variants'], message: 'Add at least one variant for variant pricing' });
  }
  if (v.pricingType === 'text' && !v.displayPriceText) {
    ctx.addIssue({ code: 'custom', path: ['displayPriceText'], message: 'displayPriceText is required for text pricing' });
  }
});

// ---------------------------------------------------------------- offers & packages

export const offerSchema = z.object({
  title: trimmed(300),
  slug,
  description: optionalText(1500),
  audience: z.enum(AUDIENCES).default('unisex'),
  group: optionalText(120),
  services: z.array(trimmed(200)).max(30).default([]),
  price: z.number().min(0).nullable().default(null),
  originalPrice: z.number().min(0).nullable().default(null),
  displayPriceText: optionalText(80),
  badge: optionalText(60),
  featured: z.boolean().default(false),
  gstApplicable: z.boolean().default(true),
  gstNote: optionalText(200),
  whatsappMessage: optionalText(600),
  image: mediaRefSchema,
  branchIds: z.array(objectId).max(20).default([]),
  startsAt: z.string().datetime().nullable().default(null),
  endsAt: z.string().datetime().nullable().default(null),
  enabled: z.boolean().default(true),
  order: z.number().int().min(0).default(0),
  ctaLabel: optionalText(60).default('Book'),
}).superRefine((v, ctx) => {
  if (v.startsAt && v.endsAt && new Date(v.startsAt) > new Date(v.endsAt)) {
    ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'endsAt must be after startsAt' });
  }
  if (v.originalPrice != null && v.price != null && v.originalPrice < v.price) {
    ctx.addIssue({ code: 'custom', path: ['originalPrice'], message: 'originalPrice should be higher than the offer price' });
  }
});

export const prepaidPackageSchema = z.object({
  name: trimmed(160),
  slug,
  code: optionalText(40),
  badge: optionalText(80),
  packageLabel: optionalText(40).default('Package'),
  packageNumber: optionalText(8),
  payAmount: z.number().min(0).nullable().default(null),
  valueAmount: z.number().min(0).nullable().default(null),
  payLabel: optionalText(40).default('You Pay'),
  valueLabel: optionalText(40).default('Get Value'),
  description: optionalText(1500),
  terms: optionalText(2000),
  image: mediaRefSchema,
  featured: z.boolean().default(false),
  active: z.boolean().default(true),
  order: z.number().int().min(0).default(0),
  whatsappMessage: optionalText(600),
}).superRefine((v, ctx) => {
  if (v.payAmount != null && v.valueAmount != null && v.valueAmount < v.payAmount) {
    ctx.addIssue({ code: 'custom', path: ['valueAmount'], message: 'Package value should be at least what the customer pays' });
  }
});

// ---------------------------------------------------------------- transformations & gallery

export const transformationSchema = z.object({
  title: trimmed(200),
  subtitle: optionalText(300),
  beforeImage: mediaRefSchema,
  afterImage: mediaRefSchema,
  labelBefore: optionalText(40).default('Start'),
  labelAfter: optionalText(40).default('The Glow Up'),
  initialPosition: z.number().min(0).max(100).default(50),
  stylistName: optionalText(120),
  stylistBadge: optionalText(120),
  serviceName: optionalText(160),
  rating: z.number().min(0).max(5).default(5),
  testimonial: optionalText(1200),
  customerName: optionalText(120),
  customerLabel: optionalText(80).default('Verified Client'),
  verified: z.boolean().default(true),
  ctaLabel: optionalText(60).default('Get This Look'),
  ctaType: z.enum(CTA_TYPES).default('whatsapp'),
  ctaValue: optionalText(600),
  order: z.number().int().min(0).default(0),
  active: z.boolean().default(true),
});

export const featuredWorkSchema = z.object({
  label: optionalText(80),
  title: trimmed(200),
  category: optionalText(120),
  rating: z.number().min(0).max(5).default(5),
  image: mediaRefSchema,
  video: mediaRefSchema,
  order: z.number().int().min(0).default(0),
  enabled: z.boolean().default(true),
});

export const galleryItemSchema = z.object({
  title: optionalText(200),
  alt: optionalText(300),
  caption: optionalText(500),
  mediaId: objectId.nullable().default(null),
  mediaType: z.enum(['image', 'video']).default('image'),
  category: optionalText(80).default('salon'),
  branchId: objectId.nullable().default(null),
  featured: z.boolean().default(false),
  order: z.number().int().min(0).default(0),
  published: z.boolean().default(true),
});

export const testimonialSchema = z.object({
  quote: trimmed(1500),
  customerName: trimmed(120),
  customerLabel: optionalText(80),
  rating: z.number().min(0).max(5).default(5),
  service: optionalText(160),
  branchId: objectId.nullable().default(null),
  image: mediaRefSchema,
  verified: z.boolean().default(true),
  order: z.number().int().min(0).default(0),
  active: z.boolean().default(true),
});

// ---------------------------------------------------------------- navigation & pages

/** Menu items nest, so the schema is recursive and needs an explicit output type. */
export interface MenuItemShape {
  label: string;
  type: 'internal' | 'external';
  value: string;
  newTab: boolean;
  order: number;
  enabled: boolean;
  children?: MenuItemShape[];
}

export const menuItemSchema: z.ZodType<MenuItemShape, z.ZodTypeDef, unknown> = z.lazy(() => z.object({
  label: trimmed(120),
  type: z.enum(['internal', 'external']).default('internal'),
  value: z.string().trim().max(2048),
  newTab: z.boolean().default(false),
  order: z.number().int().min(0).default(0),
  enabled: z.boolean().default(true),
  children: z.array(menuItemSchema).max(20).optional(),
}));

export const menuSchema = z.object({
  name: trimmed(120),
  location: z.enum(MENU_LOCATIONS),
  heading: optionalText(120),
  items: z.array(menuItemSchema).max(60).default([]),
});

export const pageSectionSchema = z.object({
  key: trimmed(80),
  type: z.enum(SECTION_TYPES),
  enabled: z.boolean().default(true),
  order: z.number().int().min(0).default(0),
  data: z.record(z.unknown()).default({}),
});

/** A theme is a preset id plus per-token overrides, each checked as a colour. */
export const themeSettingsSchema = z.object({
  presetId: z.string().max(64).default('espresso-gold'),
  overrides: z.record(z.string().max(40), z.string().max(32)).default({}),
});

export const pageSchema = z.object({
  title: trimmed(300),
  slug: z.union([slug, z.literal('')]),
  path: sitePath,
  pageType: z.enum(PAGE_TYPES).default('custom'),
  audience: z.enum(AUDIENCES).nullable().default(null),
  sections: z.array(pageSectionSchema).max(40).default([]),
  published: z.boolean().default(true),
  navigationVisibility: z.boolean().default(false),
  seo: seoSchema,
  customCss: z.string().max(200_000).default(''),
  menuCategoryIds: z.array(objectId).max(60).default([]),
  galleryItemIds: z.array(objectId).max(300).default([]),
  legacyUrl: z.string().max(2048).nullable().default(null),
});

export const redirectSchema = z.object({
  source: sitePath,
  destination: z.string().trim().max(2048),
  statusCode: z.union([
    z.literal(301), z.literal(302), z.literal(307), z.literal(308), z.literal(410),
  ]).default(301),
  reason: optionalText(500),
  active: z.boolean().default(true),
}).superRefine((v, ctx) => {
  if (v.source === v.destination) {
    ctx.addIssue({ code: 'custom', path: ['destination'], message: 'A redirect cannot point at itself' });
  }
  if (v.statusCode !== 410 && !v.destination) {
    ctx.addIssue({ code: 'custom', path: ['destination'], message: 'A destination is required unless the status is 410' });
  }
});

// ---------------------------------------------------------------- settings & homepage

export const siteSettingsSchema = z.object({
  businessName: trimmed(160),
  legalName: optionalText(200),
  tagline: optionalText(300),
  logoText: optionalText(120),
  logo: mediaRefSchema,
  favicon: mediaRefSchema,
  primaryPhone: phoneE164,
  primaryPhoneDisplay: optionalText(40),
  secondaryPhone: z.union([phoneE164, z.literal('')]).default(''),
  secondaryPhoneDisplay: optionalText(40),
  whatsappNumber: phoneE164,
  email: z.string().email().or(z.literal('')).default(''),
  gstPercent: z.number().min(0).max(100).default(5),
  gstNote: optionalText(200),
  instagramUrl: z.string().max(2048).default(''),
  facebookUrl: z.string().max(2048).default(''),
  youtubeUrl: z.string().max(2048).default(''),
  twitterUrl: z.string().max(2048).default(''),
  defaultMetaTitle: optionalText(200),
  defaultMetaDescription: optionalText(400),
  googleAnalyticsId: optionalText(40),
  googleTagManagerId: optionalText(40),
  defaultOgImage: mediaRefSchema,
  copyrightText: optionalText(300),
  seoFooterText: optionalText(600),
  creditText: optionalText(200),
  creditUrl: z.string().max(2048).default(''),
  bookNowLabel: optionalText(60).default('Book Now'),
  whatsappTemplates: z.record(z.enum(WHATSAPP_TEMPLATE_KEYS), z.string().max(600)).default({}),
  footerDescription: optionalText(1500),
  contactEmailTo: z.string().email().or(z.literal('')).default(''),
});

export const tickerItemSchema = z.object({
  text: optionalText(400),
  html: optionalText(1200),
  icon: optionalText(8),
  live: z.boolean().default(false),
  liveLabel: optionalText(60),
  link: z.string().max(2048).default(''),
  enabled: z.boolean().default(true),
  order: z.number().int().min(0).default(0),
  startsAt: z.string().datetime().nullable().default(null),
  endsAt: z.string().datetime().nullable().default(null),
});

export const heroStatSchema = z.object({
  value: trimmed(20),
  label: trimmed(60),
  shortLabel: optionalText(40),
  order: z.number().int().min(0).default(0),
  enabled: z.boolean().default(true),
});

export const heroCtaSchema = z.object({
  label: trimmed(60),
  href: z.string().max(2048),
  style: z.enum(['primary', 'secondary']).default('primary'),
  type: z.enum(CTA_TYPES).default('link'),
});

export const servicePillSchema = z.object({
  title: trimmed(120),
  subtitle: optionalText(120),
  image: mediaRefSchema,
  href: z.string().max(2048).default(''),
  order: z.number().int().min(0).default(0),
  enabled: z.boolean().default(true),
});

export const curatedCardSchema = z.object({
  title: trimmed(120),
  subtitle: optionalText(120),
  href: z.string().max(2048).default(''),
  image: mediaRefSchema,
  alt: optionalText(300),
  order: z.number().int().min(0).default(0),
  enabled: z.boolean().default(true),
});

export const homepageSchema = z.object({
  ticker: z.object({
    enabled: z.boolean().default(true),
    speedSeconds: z.number().min(5).max(300).default(55),
    items: z.array(tickerItemSchema).max(40).default([]),
  }),
  hero: z.object({
    ghostText: optionalText(40),
    eyebrow: optionalText(200),
    headingLines: z.array(trimmed(160)).max(6).default([]),
    headingAccent: optionalText(120),
    description: optionalText(800),
    descriptionStrong: optionalText(400),
    tags: z.array(trimmed(60)).max(20).default([]),
    openPillTag: optionalText(60),
    openPillText: optionalText(200),
    ctas: z.array(heroCtaSchema).max(4).default([]),
    stats: z.array(heroStatSchema).max(8).default([]),
    badgeLuxuryLabel: optionalText(60),
    badgeLuxuryValue: optionalText(60),
    badgeRatingLabel: optionalText(60),
    badgeRatingValue: optionalText(20),
    badgeBranchTag: optionalText(60),
    badgeBranchName: optionalText(80),
    badgeBranchSub: optionalText(80),
    carousel: z.array(mediaRefSchema).max(12).default([]),
  }),
  curatedServices: z.object({
    enabled: z.boolean().default(true),
    title: optionalText(120),
    titleAccent: optionalText(80),
    cards: z.array(curatedCardSchema).max(8).default([]),
  }),
  comboOffers: z.object({
    enabled: z.boolean().default(true),
    eyebrow: optionalText(120),
    title: optionalText(200),
    titleAccent: optionalText(120),
    subtitle: optionalText(600),
    ghostText: optionalText(40),
    ctaLabel: optionalText(80),
    groups: z.array(z.object({
      key: trimmed(60),
      heading: trimmed(160),
      icon: optionalText(8),
      gstNote: optionalText(200),
      order: z.number().int().min(0).default(0),
    })).max(10).default([]),
  }),
  transformations: z.object({
    enabled: z.boolean().default(true),
    title: optionalText(160),
    titleAccent: optionalText(80),
    subtitle: optionalText(400),
  }),
  featuredWork: z.object({
    enabled: z.boolean().default(true),
    title: optionalText(160),
    titleAccent: optionalText(80),
  }),
  packages: z.object({
    enabled: z.boolean().default(true),
    title: optionalText(160),
    titleAccent: optionalText(80),
  }),
  servicePills: z.object({
    enabled: z.boolean().default(true),
    speedSeconds: z.number().min(5).max(300).default(40),
    items: z.array(servicePillSchema).max(30).default([]),
  }),
  imageCarousel: z.object({
    enabled: z.boolean().default(true),
    items: z.array(mediaRefSchema).max(60).default([]),
  }),
  locations: z.object({
    enabled: z.boolean().default(true),
    tag: optionalText(60),
    title: optionalText(200),
    titleAccent: optionalText(80),
    subtitle: optionalText(600),
  }),
  seo: seoSchema,
});

// ---------------------------------------------------------------- media, auth, contact

export const mediaUpdateSchema = z.object({
  alt: optionalText(500),
  caption: optionalText(1000),
  title: optionalText(300),
  category: z.enum(MEDIA_CATEGORIES).optional(),
  branchIds: z.array(objectId).max(20).optional(),
  tags: z.array(trimmed(60)).max(30).optional(),
});

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(8).max(200),
});

export const adminUserCreateSchema = z.object({
  name: trimmed(160),
  email: z.string().trim().toLowerCase().email(),
  password: z.string()
    .min(12, 'Use at least 12 characters')
    .max(200)
    .regex(/[a-z]/, 'Include a lowercase letter')
    .regex(/[A-Z]/, 'Include an uppercase letter')
    .regex(/\d/, 'Include a number'),
  role: z.enum(USER_ROLES).default('EDITOR'),
  active: z.boolean().default(true),
});

export const adminUserUpdateSchema = adminUserCreateSchema.partial().extend({
  password: adminUserCreateSchema.shape.password.optional(),
});

export const contactInquirySchema = z.object({
  name: z.string().trim().min(2, 'Please enter your name').max(160),
  phone: z.string().trim().regex(/^[+]?[\d\s\-()]{7,20}$/, 'Please enter a valid phone number'),
  email: z.string().trim().toLowerCase().email('Please enter a valid email').or(z.literal('')).default(''),
  branchId: objectId.nullable().default(null),
  service: optionalText(200),
  message: z.string().trim().min(2, 'Please tell us how we can help').max(3000),
  sourcePage: optionalText(500),
  /** Honeypot: bots fill it in, humans never see it. */
  company: z.string().max(0, 'Rejected').optional().default(''),
});

export const enquiryUpdateSchema = z.object({
  status: z.enum(ENQUIRY_STATUSES).optional(),
  notes: optionalText(3000).optional(),
});

// ---------------------------------------------------------------- query helpers

export const listQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(25),
  q: z.string().trim().max(200).optional(),
  sort: z.string().trim().max(80).optional(),
  order: z.enum(['asc', 'desc']).default('asc'),
  audience: z.enum(AUDIENCES).optional(),
  categoryId: objectId.optional(),
  branchId: objectId.optional(),
  category: z.string().trim().max(80).optional(),
  type: z.enum(MEDIA_TYPES).optional(),
  status: z.string().trim().max(40).optional(),
  active: z.enum(['true', 'false']).optional(),
  published: z.enum(['true', 'false']).optional(),
  featured: z.enum(['true', 'false']).optional(),
});

export const reorderSchema = z.object({
  ids: z.array(objectId).min(1).max(500),
});

export const bulkIdsSchema = z.object({
  ids: z.array(objectId).min(1).max(500),
});

export const bulkCategorySchema = bulkIdsSchema.extend({
  category: trimmed(80),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type ContactInquiryInput = z.infer<typeof contactInquirySchema>;
export type ListQuery = z.infer<typeof listQuerySchema>;
